import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceDepannage = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Dépannage & support ponctuel à distance
          </h1>
          <p className="text-xl text-gray-600">
            Je prends en charge vos incidents et demandes ponctuelles à distance : problèmes de connexion, lenteurs, messages d'erreur, imprimantes, accès à distance, etc.
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              <strong>Vous gagnez du temps :</strong> un point de contact qui parle votre langage, des solutions concrètes, et une intervention sur site lorsque c'est nécessaire.
            </p>
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Exemples d'interventions courantes</h2>
          <div className="space-y-3">
            {[
              'Problèmes de connexion internet ou réseau',
              'Lenteurs inhabituelles sur un poste ou un serveur',
              'Messages d\'erreur logiciels',
              'Difficultés d\'accès à des fichiers ou applications',
              'Configuration d\'imprimantes et périphériques',
              'Mises en place d\'accès distants (VPN, bureau à distance)',
              'Installations et configurations de logiciels',
              'Restauration de fichiers depuis les sauvegardes',
              'Support aux utilisateurs sur des outils spécifiques'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Les bénéfices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Réactivité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Intervention rapide à distance pour résoudre vos problèmes</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Économie de temps</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Pas besoin d'attendre une intervention sur site pour la plupart des cas</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Communication claire</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Explications en langage simple, sans jargon technique</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Flexibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Intervention ponctuelle ou régulière selon vos besoins</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Intervention sur mesure</h3>
            <p className="text-gray-600 mb-4">
              Chaque intervention est facturée au temps passé ou selon un forfait adapté à votre volume de demandes. 
              Je peux aussi vous proposer un contrat de support avec un nombre d'heures prédéfini pour plus de sérénité.
            </p>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Besoin d'aide rapidement ?</h3>
          <p className="text-gray-600 mb-6">
            Contactez-moi pour discuter de votre problème et trouver une solution ensemble.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};